import { Autocomplete, TextField } from "@mui/material";
import { useCallback, useState } from "react";
import { cancelPrevious, CancelState } from "./cancelable";
import { createAxios } from "./axios";
import { KeysOfType, delay, landCodes, sanitize } from "./utils";
import { UseEntityStore } from "./entityStore";
import { useDefaultValues } from "./defaultValues";
import { getMostImportantValidationMessage, propTextField, recommendedColor, useGenericTextValidator } from "./entityStoreForm";
import { getResource } from "./localization";

function getText(key: string, defaultMessage?: string) {
    return getResource("Ui.Customer." + key, defaultMessage) as string;
}

interface Address {
    fieldType: string;
    values: {
        number?: string;
        street?: string;
        municipality?: string;
        district?: string;
        region?: string;
        zip?: string;
        city?: string;
    };
    flags?: {
        wholeAddress?: "true";
    };
}

export interface EntityAddress {
    strt1: string;
    strt2: string;
    zipCode: string;
    city: string;
}

type ValueType = "STREET" | "STREET_AND_NUMBER" | "CITY" | "ZIP";

export interface AddressInputProps {
    disabled?: boolean;
    readOnly?: boolean;
    suggestionsEnabled?: boolean;
    bgColor?: string | false | null;
}

function AddressInput<T extends EntityAddress, U extends KeysOfType<EntityAddress, string | null | undefined>>(props: {
    useEntityStore: UseEntityStore<T>;
    prop: U;
    label: string;
    suggestions: Address[];
    getOptionLabel: (address: Address) => string;
    clearSuggestions(): void;
    updateSuggestionsDelayed(delayMs: number): Promise<void>;
} & AddressInputProps) {
    const def = useDefaultValues(p => p.clientData);
    const suggestionsEnabled = props.suggestionsEnabled ?? landCodes("CZ", "SK", "HU").includes(def.landCode);
    useGenericTextValidator(props.useEntityStore, props.prop as any);
    async function updateValue(value: string | Address | null) {
        const { clientData: entity, setClientDataProp, setClientData } = props.useEntityStore.getState();
        if (typeof value === "string" || value == null) {
            var stringValue = value || "";
            if (props.prop === "strt1") {
                if (stringValue === entity.strt1) {
                    return;
                }
                setClientDataProp("strt1", stringValue);
            } else if (props.prop === "city") {
                if (stringValue === entity.city) {
                    return;
                }
                setClientDataProp("city", stringValue);
            } else if (props.prop === "zipCode") {
                if (stringValue === entity.zipCode) {
                    return;
                }
                setClientDataProp("zipCode", stringValue);
            }
            if (stringValue !== "" && suggestionsEnabled) {
                await props.updateSuggestionsDelayed(200);
            } else {
                props.clearSuggestions();
            }
        } else if (typeof value === "object") {
            setClientData({
                ...entity,
                strt1: value.values.street || entity.strt1,
                strt2: value.values.number || entity.strt2,
                city: value.values.city || entity.city,
                zipCode: value.values.zip || entity.zipCode,
            });
            props.clearSuggestions();
        }
    }

    const store = props.useEntityStore(p => ({
        propValue: p.clientData[props.prop] as string | null | undefined,
        ...getMostImportantValidationMessage(p, props.prop),
    }));

    if (!suggestionsEnabled) {
        return propTextField(props.useEntityStore)(props.prop as any, props.label, {
            disabled: props.disabled, readOnly: props.readOnly, bgColor: props.bgColor,
        });
    }

    return <Autocomplete
        readOnly={props.readOnly}
        onOpen={() => { props.clearSuggestions(); !props.disabled && !props.readOnly && suggestionsEnabled && props.updateSuggestionsDelayed(0); }}
        onInputChange={(event, value) => {
            if (event?.type === "click") { return; }
            updateValue(value);
        }}
        onChange={(_event, value) => updateValue(value)}
        value={store.propValue || ""}
        getOptionLabel={item => typeof item === "string" || item.values == null ? store.propValue || "" : props.getOptionLabel(item)}
        options={props.suggestions}
        renderInput={params => <TextField
            error={store.firstMessage != null} helperText={store.firstMessage}
            {...{
                ...params,
                inputProps: { ...params.inputProps, value: store.propValue || "", autoComplete: "new-password", disabled: props.disabled },
                sx: {
                    ...props.bgColor ? { "& .MuiInputBase-root": { backgroundColor: props.bgColor } } : {},
                    ...store.firstMessageType === "error" ? {} :
                        store.firstMessageType === "recommended" ? {
                            "& .MuiFormLabel-root.Mui-error": {
                                color: p => `${recommendedColor} !important`,
                            },
                            "& .Mui-error .MuiOutlinedInput-notchedOutline": {
                                borderColor: p => `${recommendedColor} !important`,
                            },
                            "& .MuiFormHelperText-root.Mui-error": {
                                color: p => `${recommendedColor} !important`,
                            }
                        } : {},
                }
            }}
            label={props.label} autoComplete="new-password" />}
        disabled={props.disabled}
        filterOptions={p => p}
        freeSolo
        onBlur={() => {
            props.useEntityStore.getState().setClientDataProp(props.prop, sanitize(store.propValue ?? "") as T[U]);
        }}
    />
}

export function useAddressSuggestions<T extends EntityAddress>(
    useEntityStore: UseEntityStore<T>) {
    const [suggestions, setSuggestions] = useState<Address[]>([]);
    const landCode = useDefaultValues(p => p.clientData.landCode);

    function clearSuggestions() {
        setSuggestions([]);
    }

    async function updateSuggestions(state: CancelState, valueType: ValueType, delayMs: number) {
        const entity = useEntityStore.getState().clientData;
        await delay(delayMs);
        if (state.canceled) {
            return;
        }
        var inputValue =
            valueType === "STREET" ? entity.strt1 :
                valueType === "STREET_AND_NUMBER" ? entity.strt1 :
                    valueType === "CITY" ? entity.city :
                        valueType === "ZIP" ? entity.zipCode :
                            undefined;
        if (valueType !== "ZIP" && valueType !== "CITY" && inputValue != null && inputValue.length < 2) {
            clearSuggestions();
            return;
        }
        const response = await createAxios().post("AddressInfo", {
            fieldType: valueType,
            values: {
                streetAndNumber: entity.strt1,
                city: entity.city,
                zip: entity.zipCode,
            }
        });
        if (state.canceled) {
            return;
        }
        setSuggestions(response.data.suggestions);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const updateSuggestionsDelayed = useCallback(cancelPrevious(updateSuggestions), []);

    function renderStreetInput(props?: AddressInputProps) {
        return <AddressInput label={getText("Strt1", "Street")} prop="strt1" {...props} clearSuggestions={clearSuggestions} suggestions={suggestions}
            updateSuggestionsDelayed={delayMs => updateSuggestionsDelayed(landCode === "HU" ? "STREET" : "STREET_AND_NUMBER", delayMs)} useEntityStore={useEntityStore}
            getOptionLabel={item => [
                [item.values.street, item.values.number].filter(p => p).join(" "),
                item.values.city,
                item.values.zip
            ].filter(p => p).join(", ")}
        />
    }

    function renderCityInput(props?: AddressInputProps) {
        return <AddressInput label={getText("City", "City")} prop="city" {...props} clearSuggestions={clearSuggestions} suggestions={suggestions}
            updateSuggestionsDelayed={delayMs => updateSuggestionsDelayed("CITY", delayMs)} useEntityStore={useEntityStore}
            getOptionLabel={item => item.values.city || ""}
        />
    }

    function renderZipInput(props?: AddressInputProps) {
        return <AddressInput label={getText("ZipCode", "ZIP code")} prop="zipCode" {...props} clearSuggestions={clearSuggestions} suggestions={suggestions}
            updateSuggestionsDelayed={delayMs => updateSuggestionsDelayed("ZIP", delayMs)} useEntityStore={useEntityStore}
            getOptionLabel={item => item.values.zip || ""}
        />
    }

    return { renderStreetInput, renderCityInput, renderZipInput };
}